import { Link, useNavigate } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Check, Download, PackageCheck, Pencil, Receipt, XCircle } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiDownload, isApiConfigured } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatMoney, labelFor, PAYMENT_TERMS } from "@/features/clients/constants";
import { useSalesOrder, useUpdateSalesOrderStatus } from "@/features/sales-orders/api";
import {
  SO_STATUSES,
  canEditLines,
  canFulfill,
  labelFor as soLabelFor,
  remainingWeight,
  statusTone,
} from "@/features/sales-orders/constants";
import { useSalesOrderFulfillments } from "@/features/sales-orders/fulfillment-api";
import type { SalesOrderStatus } from "@/features/sales-orders/types";

export function SalesOrderDetailPage({ soId }: { soId: string }) {
  return (
    <ErrorBoundary title="Sales order detail crashed">
      <SalesOrderDetailInner soId={soId} />
    </ErrorBoundary>
  );
}

function SalesOrderDetailInner({ soId }: { soId: string }) {
  const navigate = useNavigate();
  const { data: so, isLoading, error } = useSalesOrder(soId);
  const { data: fulfillments = [] } = useSalesOrderFulfillments(soId);
  const updateStatus = useUpdateSalesOrderStatus();

  if (isLoading) {
    return <p className="py-16 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  if (error || !so) {
    return (
      <EmptyState
        title="Sales order not found"
        description={(error as Error)?.message ?? "Unknown order"}
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/sales-orders">Back to list</Link>
          </Button>
        }
      />
    );
  }

  async function setStatus(status: SalesOrderStatus) {
    await updateStatus.mutateAsync({ id: soId, status });
  }

  async function downloadPdf() {
    try {
      await apiDownload(`/pdf/sales-orders/${soId}`, `${so?.so_number || "so"}.pdf`);
    } catch (e) {
      toast.error((e as Error).message || "PDF download failed");
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Button asChild variant="ghost" size="icon-sm">
            <Link to="/sales-orders">
              <ArrowLeft />
            </Link>
          </Button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-mono text-lg font-semibold">{so.so_number}</h1>
              <StatusDot tone={statusTone(so.status)}>
                {soLabelFor(SO_STATUSES, so.status)}
              </StatusDot>
            </div>
            <p className="text-xs text-muted-foreground">
              {so.client_code} · {so.client_name} · {format(parseISO(so.issue_date), "dd MMM yyyy")}
              {so.due_date ? ` · due ${format(parseISO(so.due_date), "dd MMM yyyy")}` : ""}
              {so.qt_number ? ` · from ${so.qt_number}` : ""}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {isApiConfigured() ? (
            <Button type="button" variant="outline" size="sm" onClick={() => void downloadPdf()}>
              <Download />
              Download PDF
            </Button>
          ) : null}
          {canEditLines(so.status) || so.status === "confirmed" ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: "/sales-orders/$soId/edit", params: { soId } })}
            >
              <Pencil />
              Edit
            </Button>
          ) : null}
          {so.status === "draft" ? (
            <Button
              type="button"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("confirmed")}
            >
              <Check />
              Confirm
            </Button>
          ) : null}
          {canFulfill(so.status) ? (
            <Button
              type="button"
              size="sm"
              onClick={() => navigate({ to: "/sales-orders/$soId/fulfill", params: { soId } })}
            >
              <PackageCheck />
              Fulfill stock
            </Button>
          ) : null}
          {!["draft", "cancelled"].includes(so.status) ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: "/invoices/new", search: { so: soId } })}
            >
              <Receipt />
              Create invoice
            </Button>
          ) : null}
          {fulfillments.length > 0 ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: "/dispatch/new", search: { so: soId } })}
            >
              Dispatch
            </Button>
          ) : null}
          {["draft", "confirmed", "in_progress", "partial"].includes(so.status) ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={updateStatus.isPending}
              onClick={() => setStatus("cancelled")}
            >
              <XCircle />
              Cancel
            </Button>
          ) : null}
        </div>
      </header>

      <div className="grid gap-3 text-xs md:grid-cols-2">
        <section className="rounded border p-3">
          <h2 className="mb-1 font-medium">Bill to</h2>
          <pre className="whitespace-pre-wrap font-sans text-muted-foreground">
            {so.bill_to_text || "—"}
          </pre>
        </section>
        <section className="rounded border p-3">
          <h2 className="mb-1 font-medium">Commercial</h2>
          <dl className="space-y-1 text-muted-foreground">
            <div className="flex justify-between gap-2">
              <dt>Payment</dt>
              <dd>{labelFor(PAYMENT_TERMS, so.payment_terms)}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>Client PO ref</dt>
              <dd>{so.client_po_ref || "—"}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>Ship to</dt>
              <dd className="max-w-[60%] text-right">{so.ship_to_text || "—"}</dd>
            </div>
          </dl>
        </section>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Material</TableHead>
              <TableHead>Grade</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Wt</TableHead>
              <TableHead className="text-right">Fulfilled</TableHead>
              <TableHead className="text-right">Rate</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(so.lines ?? []).map((line) => (
              <TableRow key={line.id} className="text-xs">
                <TableCell>{line.line_no}</TableCell>
                <TableCell>
                  {line.material_name}
                  {line.shape_label ? ` · ${line.shape_label}` : ""}
                </TableCell>
                <TableCell>{line.grade_name ?? "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{line.qty || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {line.weight > 0 ? line.weight : "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {line.fulfilled_weight > 0 ? (
                    <>
                      {line.fulfilled_weight}
                      {remainingWeight(line) > 0 ? (
                        <span className="block text-2xs">rem {remainingWeight(line)}</span>
                      ) : null}
                    </>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{line.unit_rate}</TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {formatMoney(line.line_subtotal)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <footer className="flex flex-wrap justify-end gap-4 text-xs">
        <div className="space-y-1 text-right">
          <div>
            Subtotal <strong className="tabular-nums">{formatMoney(so.subtotal)}</strong>
          </div>
          {so.discount > 0 ? (
            <div>
              Discount <strong className="tabular-nums">−{formatMoney(so.discount)}</strong>
            </div>
          ) : null}
          <div>
            Tax <strong className="tabular-nums">{formatMoney(so.tax_total)}</strong>
          </div>
          <div className="text-sm">
            Grand total{" "}
            <strong className="tabular-nums text-primary">{formatMoney(so.grand_total)}</strong>
          </div>
        </div>
      </footer>

      {fulfillments.length ? (
        <section className="rounded border p-3 text-xs">
          <h2 className="mb-2 font-medium">Fulfillment history</h2>
          <ul className="space-y-1 text-muted-foreground">
            {fulfillments.slice(0, 8).map((f) => (
              <li key={f.id}>
                L{f.line_no} · {f.batch_no} · {f.weight} kg
                {f.quantity > 0 ? ` · ${f.quantity} pcs` : ""} ·{" "}
                {format(parseISO(f.fulfilled_at), "dd MMM yyyy HH:mm")}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
